import { faCopy, faTrash } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import LoadingButton from '@mui/lab/LoadingButton'
import { AlertTitle, Button, IconButton, TextareaAutosize, Tooltip, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import { FC, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
// @ts-ignore
import * as Yup from 'yup'

import { useApiKeyCreate } from '@/api/api-keys/create'
import { useDeleteApiKey } from '@/api/api-keys/delete'
import { useApiKeys } from '@/api/api-keys/get'
import { IP_REGEX, US_DATE_WITH_MONTH_NAME } from '@/api/constants'
import ApiKeyIcon from '@/assets/images/Integrations/FormStages/ApiKey.svg'
import DisksIcon from '@/assets/images/Integrations/FormStages/Disks.svg'
import KeyIcon from '@/assets/images/Integrations/FormStages/Key.svg'
import PartnerApiKey from '@/assets/images/Integrations/Types/PartnerApiKey.svg'
import Modal from '@/common/components/Modal'
import Stepper from '@/common/components/Stepper'
import Step from '@/common/components/Stepper/Step'
import useToast from '@/common/hooks/useToast'
import { theme } from '@/theme/theme'
import TextField from '../Inputs/TextFieldV2'
import SimpleTable from '../Tables/SimpleTable'
import { StyledButtonWrapper } from './IntegrationModal'

interface PartnerApiModalProps {
  isOpen: boolean
  onClose: () => void
}

const PartnerApiModal: FC<PartnerApiModalProps> = ({ isOpen, onClose }) => {
  const IP_CELL_LIMIT = 6

  const { t } = useTranslation()
  const { successToast, errorToast } = useToast()

  const [newApiKey, setNewApiKey] = useState<string>('')

  const initialValues = {
    key_name: '',
    allowlisted_ips: '',
  }

  const validationSchema = Yup.object().shape({
    key_name: Yup.string().required('API name is required'),
    allowlisted_ips: Yup.string()
      .required('At least one IP address is required')
      .test('valid-ip-list', 'One or more IP addresses are not valid', (value: string) => {
        if (!value) return false

        const ips = value
          .split('\n')
          .map((ip: string) => ip.trim())
          .filter((ip: string) => ip.length > 0) // remove empty lines

        if (ips.length === 0) return false

        return ips.every((ip: string) => IP_REGEX.test(ip))
      })
      .test('unique-ip-list', 'IP addresses must be unique', (value: string) => {
        if (!value) return false

        const ips = value
          .split('\n')
          .map((ip) => ip.trim())
          .filter((ip) => ip.length > 0)

        const uniqueIps = new Set(ips)
        return uniqueIps.size === ips.length
      }),
  })

  const { data: apiKeysData } = useApiKeys()
  const { mutateAsync: createApiKey } = useApiKeyCreate()
  const { mutateAsync: deleteApiKey } = useDeleteApiKey()

  const handleClose = () => {
    onClose()
    setNewApiKey('')
  }

  const handleApiKeyDelete = async (id: string) => {
    try {
      await deleteApiKey(id)
      setNewApiKey('')
      successToast(t('settings.integrations.deleteSuccessMessage'))
    } catch (e) {
      errorToast(t('settings.integrations.deleteErrorMessage'))
    }
  }

  const onSubmit = async (values: typeof initialValues, { resetForm }: any) => {
    try {
      const response = await createApiKey({
        ...values,
        allowlisted_ips: [...new Set(values.allowlisted_ips.split('\n'))],
      })
      setNewApiKey(response.key)
      resetForm()
    } catch (e) {
      errorToast(t('settings.integrations.errorMessage'))
    }
  }

  const copyToken = () => {
    navigator.clipboard.writeText(newApiKey)
    successToast(t('settings.integrations.copyMessage'))
  }

  const columns = useMemo(
    () => [
      {
        Header: t('settings.integrations.tableHeaderName'),
        accessor: 'key_name',
        disableSortBy: true,
      },
      {
        Header: t('settings.integrations.tableHeaderCreatedOn'),
        Cell: ({ row }) => {
          return <>{dayjs(row.original.created_at).format(US_DATE_WITH_MONTH_NAME)}</>
        },
        disableSortBy: true,
      },
      {
        Header: t('settings.integrations.tableHeaderLastUsedOn'),
        Cell: ({ row }) => {
          return (
            <>
              {dayjs(row.original.last_used_at).isValid()
                ? dayjs(row.original.last_used_at).format(US_DATE_WITH_MONTH_NAME)
                : '-'}
            </>
          )
        },
        disableSortBy: true,
      },
      {
        Header: t('settings.integrations.tableHeaderCreatedBy'),
        accessor: 'created_by',
        disableSortBy: true,
      },
      {
        Header: t('settings.integrations.tableHeaderAllowlistedIps'),
        Cell: ({ row }) => {
          return (
            <Tooltip
              title={row.original.allowlisted_ips.join(', ')}
              disableHoverListener={row.original.allowlisted_ips.length <= IP_CELL_LIMIT}>
              <>
                {row.original.allowlisted_ips.slice(0, IP_CELL_LIMIT).join(', ')}
                {row.original.allowlisted_ips.length > IP_CELL_LIMIT && <>...</>}
              </>
            </Tooltip>
          )
        },
        disableSortBy: true,
      },
      {
        Header: ' ',
        Cell: ({ row }) => {
          return (
            <IconButton
              onClick={() => {
                handleApiKeyDelete(row.original.id)
              }}
              aria-label="delete">
              <FontAwesomeIcon icon={faTrash} fontSize={16} color={theme.palette.black} />
            </IconButton>
          )
        },
      },
    ],
    [t]
  )

  return (
    <Modal
      title={t('settings.integrations.partnerApiModalTitle')}
      isOpen={isOpen}
      onClose={handleClose}
      size="mediumLarge"
      withCloseButton>
      <Box sx={{ p: '16px', overflowY: 'auto', maxHeight: '90vh' }}>
        <Stepper>
          <Step label={t('settings.integrations.partnerApiStep1')} icon={DisksIcon} active>
            <Box display="flex" gap={2}>
              <Box sx={{ padding: '14px', border: '2px solid cyan', borderRadius: '16px' }}>
                <img src={PartnerApiKey} alt="Partner API Key" />
              </Box>
              <Alert severity="info">
                <AlertTitle>{t('settings.integrations.modalStep1Title')}</AlertTitle>
                <Trans
                  i18nKey={'settings.integrations.modalStep1Message'}
                  components={{
                    1: <a href="https://catalog.cywareness.io/user_manual" target="_blank" />,
                  }}></Trans>
              </Alert>
            </Box>
          </Step>
          <Step label={t('settings.integrations.partnerApiStep2')} icon={KeyIcon} active>
            <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
              {({ isSubmitting, values }) => (
                <Form>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography>{t('settings.integrations.modalStep2Title')}</Typography>
                    <TextField required type="text" label={t('settings.integrations.fieldApiName')} name="key_name" />
                    <TextField
                      multiline
                      rows={3}
                      label={t('settings.integrations.fieldAllowlistedIp')}
                      name="allowlisted_ips"
                    />
                    <Box display="flex" gap={theme.spacing(2)} alignItems="center">
                      <LoadingButton
                        startIcon={<img src={PartnerApiKey} width="16" height="16" />}
                        disabled={!!apiKeysData?.length}
                        loading={isSubmitting}
                        variant="contained"
                        type="submit">
                        {t('settings.integrations.submitButton')}
                      </LoadingButton>
                      {newApiKey && (
                        <>
                          <Typography fontSize={14} fontFamily={'monospace'}>
                            {newApiKey}
                          </Typography>
                          <IconButton onClick={copyToken}>
                            <Tooltip title={t('settings.integrations.copyButton')} placement="top-start">
                              <FontAwesomeIcon icon={faCopy} fontSize={16} color={theme.palette.black} />
                            </Tooltip>
                          </IconButton>
                        </>
                      )}
                    </Box>

                    {!!newApiKey && (
                      <Alert severity="success">
                        <AlertTitle>{t('settings.integrations.successTitle')}</AlertTitle>
                        {t('settings.integrations.successMessage')}
                      </Alert>
                    )}

                    {!!apiKeysData?.length && !newApiKey && (
                      <Alert severity="info">
                        <AlertTitle>{t('settings.integrations.infoTitle')}</AlertTitle>
                        {t('settings.integrations.infoMessage')}
                      </Alert>
                    )}
                  </Box>
                </Form>
              )}
            </Formik>
          </Step>
          <Step label={t('settings.integrations.partnerApiStep3')} icon={ApiKeyIcon} active>
            {apiKeysData && (
              <SimpleTable
                stickyHeader
                enableCheckbox={false}
                columns={columns}
                data={apiKeysData}
                setData={() => {}}
                noResultsContent={t('settings.integrations.tableNoResults')}
              />
            )}
          </Step>
        </Stepper>
        <StyledButtonWrapper>
          <Button variant="outlined" type="button" onClick={handleClose}>
            {t('settings.integrations.done')}
          </Button>
        </StyledButtonWrapper>
      </Box>
    </Modal>
  )
}

export default PartnerApiModal
